
import { useState } from 'react';

interface TourBookingProps {
  tourTitle: string;
  price: number;
}

export default function TourBooking({ tourTitle, price }: TourBookingProps) {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [guests, setGuests] = useState(1);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const urlEncodedData = new URLSearchParams();
    formData.forEach((value, key) => {
      urlEncodedData.append(key, value.toString());
    });
    urlEncodedData.append('tour', tourTitle);
    urlEncodedData.append('totalPrice', `₱${(price * guests).toLocaleString()}`);

    try {
      const response = await fetch('https://readdy.ai/api/form/d63n1a2bdoed7v0v8qmg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setGuests(1);
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <section className="py-16 px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="w-3 h-3 bg-[#1A237E] rotate-45"></span>
            <span className="text-sm font-bold text-[#1A237E] tracking-wider">BOOKING</span>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">예약하기</h2>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="bg-[#FAFAF8] rounded-2xl p-8">
            <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{tourTitle}</h3>
                <p className="text-sm text-gray-500 mt-1">1인당 ₱{price.toLocaleString()}</p>
              </div>
              <div className="text-right">
                <div className="text-sm text-gray-500">총 예상 금액</div>
                <div className="text-2xl font-bold text-[#00BCD4]">₱{(price * guests).toLocaleString()}</div>
              </div>
            </div>

            <form className="space-y-5" data-readdy-form id="tour-booking-form" onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">이름 *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="홍길동"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm bg-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">이메일 *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm bg-white"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">연락처 *</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="+82 10-1234-5678"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm bg-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">인원 수 *</label>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setGuests(Math.max(1, guests - 1))}
                      className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-600"
                    >
                      <i className="ri-subtract-line"></i>
                    </button>
                    <input
                      type="number"
                      name="guests"
                      value={guests}
                      readOnly
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-center bg-white"
                    />
                    <button
                      type="button"
                      onClick={() => setGuests(Math.min(20, guests + 1))}
                      className="w-10 h-10 flex items-center justify-center border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-600"
                    >
                      <i className="ri-add-line"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">희망 날짜 *</label>
                <input
                  type="date"
                  name="date"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">요청 사항</label>
                <textarea
                  name="message"
                  placeholder="특별한 요청 사항이 있으시면 적어주세요"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-[#00BCD4] text-sm resize-none bg-white"
                ></textarea>
                <div className="text-xs text-gray-400 mt-1 text-right">최대 500자</div>
              </div>

              <button
                type="submit"
                disabled={formStatus === 'submitting'}
                className="w-full py-3.5 bg-[#00BCD4] text-white font-semibold rounded-lg hover:bg-[#00ACC1] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-base"
              >
                {formStatus === 'submitting' ? '예약 접수 중...' : '예약 신청하기'}
              </button>

              {formStatus === 'success' && (
                <div className="text-center py-3 bg-green-50 rounded-lg text-green-600 text-sm font-medium">
                  <i className="ri-check-line mr-1"></i> 예약이 성공적으로 접수되었습니다! 곧 연락드리겠습니다.
                </div>
              )}
              {formStatus === 'error' && (
                <div className="text-center py-3 bg-red-50 rounded-lg text-red-600 text-sm font-medium">
                  전송 중 오류가 발생했습니다. 다시 시도해주세요.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
